/** A diagnosis thread as Markdown — the whole conversation, or one message.
 *
 *  Pure functions over an export model built by the chat, so the copy, the
 *  `.md` download and the PDF all say the same thing. Answers go in verbatim:
 *  they are already Markdown (`diagnostic_md`). Questions are the operator's
 *  plain text, quoted line by line so nothing in them is read as Markdown
 *  structure. */

import type { DiagnoseAttachment } from '@/api/diagnoseAttachments';
import { fileSlug } from '@/utils/download';
import { formatBytes } from '@/utils/formatBytes';

import { KIND_LABEL } from '../attachments/kind';

export interface ExportTurn {
  /** 1-based position in the thread. */
  index: number;
  taskId: string;
  question: string;
  attachments: DiagnoseAttachment[];
  createdTs: string | null;
  userEmail: string | null;
  durationSeconds: number | null;
  status: 'completed' | 'failed' | 'cancelled';
  /** Failed turns: why. */
  failedMessage?: string;
  answerMd?: string;
  /** Display label of the model (falls back to its id). */
  model?: string;
  /** What the turn spent; null when unknown. */
  costUsd?: number | null;
  toolCalls?: number;
}

export interface ExportMeta {
  /** "Booking 12345". */
  subjectTitle: string;
  /** Link back to the open thread in the console. */
  threadUrl: string | null;
  exportedAt: Date;
  exportedBy?: string | null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `2026-10-08 14:02` in the reader's local time — unambiguous, sortable. */
export function formatStamp(d: Date | string | null | undefined): string {
  if (d == null) return '';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '';
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${pad(s % 60)}s`;
}

export function formatCost(usd: number | null | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '';
  return `$${usd < 0.01 && usd > 0 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

/** Code-span a user-controlled name so Markdown can't re-read it: the fence is
 *  one backtick longer than the longest backtick run inside (CommonMark closes a
 *  span on the first run of equal length), padded when the name touches one. */
export function codeSpan(s: string): string {
  const clean = s.replace(/[\r\n]+/g, ' ');
  const longest = Math.max(0, ...(clean.match(/`+/g) ?? []).map((run) => run.length));
  const fence = '`'.repeat(longest + 1);
  const pad = longest > 0 ? ' ' : '';
  return `${fence}${pad}${clean}${pad}${fence}`;
}

export function attachmentLabel(a: DiagnoseAttachment): string {
  const facts = [KIND_LABEL[a.kind], formatBytes(a.size)];
  if (a.pages) facts.push(`${a.pages} page${a.pages > 1 ? 's' : ''}`);
  return `${codeSpan(a.filename)} (${facts.join(', ')})`;
}

function quote(text: string): string {
  return text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => (line ? `> ${line}` : '>'))
    .join('\n');
}

/** `claude-opus-5 · $0.84 · 9 tool calls · 1m 42s` */
export function turnMetaLine(t: ExportTurn): string {
  const parts = [
    t.model ?? '',
    formatCost(t.costUsd),
    t.toolCalls != null ? `${t.toolCalls} tool call${t.toolCalls === 1 ? '' : 's'}` : '',
    formatDuration(t.durationSeconds),
  ].filter(Boolean);
  return parts.join(' · ');
}

/** One question as Markdown (with the files it carried). */
export function turnQuestionMarkdown(t: ExportTurn): string {
  const out = [quote(t.question)];
  if (t.attachments.length > 0) out.push('', `Attachments: ${t.attachments.map(attachmentLabel).join(', ')}`);
  return out.join('\n');
}

/** One answer as Markdown: the diagnosis verbatim, then the run's facts. */
export function turnAnswerMarkdown(t: ExportTurn): string {
  if (t.status === 'failed') return `_The diagnosis failed: ${t.failedMessage || 'unknown error'}_`;
  if (t.status === 'cancelled') return '_This turn was cancelled._';
  const meta = turnMetaLine(t);
  return [(t.answerMd ?? '').trim(), ...(meta ? ['', `_${meta}_`] : [])].join('\n');
}

export function threadToMarkdown(meta: ExportMeta, turns: ExportTurn[]): string {
  const header = [
    meta.threadUrl ? `Thread: ${meta.threadUrl}` : '',
    `exported ${formatStamp(meta.exportedAt)}${meta.exportedBy ? ` by ${meta.exportedBy}` : ''}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const total = turns.reduce((sum, t) => sum + (t.costUsd ?? 0), 0);
  const blocks = [`# AI diagnosis — ${meta.subjectTitle}`, '', header];
  if (total > 0) blocks.push('', `${turns.length} question${turns.length === 1 ? '' : 's'} · total cost ${formatCost(total)}`);
  for (const t of turns) {
    const who = [formatStamp(t.createdTs), t.userEmail ?? ''].filter(Boolean).join(' — ');
    blocks.push(
      '',
      `## Turn ${t.index}${who ? ` — ${who}` : ''}`,
      '',
      '### Question',
      '',
      turnQuestionMarkdown(t),
      '',
      '### Answer',
      '',
      turnAnswerMarkdown(t),
    );
  }
  return `${blocks.join('\n')}\n`;
}

/** `ai-diagnosis-booking-12345-2026-10-08[-turn-2-answer].md` */
export function exportFileName(subjectTitle: string, ext: 'md' | 'pdf', at: Date, part?: string): string {
  const date = formatStamp(at).slice(0, 10);
  return `ai-diagnosis-${fileSlug(subjectTitle)}-${date}${part ? `-${fileSlug(part)}` : ''}.${ext}`;
}
